import { useState, useCallback } from "react";
import { apiFetch, apiMutate } from "@/lib/api";
import type { ConversationMessage, ConversationDetail, ToolCallInfo } from "./use-conversations";

// Re-export types that DataAnalyst.tsx uses
export type { ToolCallInfo };

export interface Attachment {
  type: "image" | "document" | "text";
  mediaType: string;  // e.g. "image/jpeg", "application/pdf", "text/csv"
  data: string;       // base64 for images/PDFs, raw text for CSV/TXT
  filename: string;
  sizeBytes: number;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  attachments?: Array<{ type: string; mediaType: string; filename: string; sizeBytes: number }>;
  tool_calls?: ToolCallInfo[];
  model?: string;
  duration_ms?: number;
  timestamp: number;
  conversationId?: string;
  follow_up_suggestions?: string[];
}

export interface StreamingState {
  content: string;
  liveTools: Array<{
    tool: string;
    input_summary: string;
    status: "running" | "done";
    row_count?: number;
    execution_ms?: number;
  }>;
  liveCharts: Array<{ spec: unknown; sqlQuery?: string }>;
}

interface SendMessageResponse {
  id: string;
  role: "assistant";
  content: string;
  model: string;
  tool_calls: ToolCallInfo[];
  tool_call_count: number;
  duration_ms: number;
  conversation_title?: string;
  follow_up_suggestions?: string[];
}

interface ModelInfo {
  id: string;
  name: string;
  provider: string;
  available?: boolean;
}

let messageCounter = 0;
function nextId() {
  return `msg-${++messageCounter}-${Date.now()}`;
}

/** Convert a persisted ConversationMessage into the local ChatMessage shape. */
function toLocalMessage(m: ConversationMessage, conversationId: string): ChatMessage {
  return {
    id: m.id,
    role: m.role,
    content: m.content,
    attachments: m.attachments_meta ? JSON.parse(m.attachments_meta) : undefined,
    tool_calls: m.tool_calls ?? undefined,
    model: m.model ?? undefined,
    duration_ms: m.duration_ms ?? undefined,
    timestamp: new Date(m.created_at).getTime(),
    conversationId,
  };
}

/** Parse SSE events from a ReadableStream. */
async function readSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (event: string, data: Record<string, unknown>) => void,
) {
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split("\n\n");
    buffer = parts.pop()!;

    for (const part of parts) {
      const eventMatch = part.match(/^event:\s*(.+)/m);
      const dataMatch = part.match(/^data:\s*(.+)/m);
      if (!eventMatch || !dataMatch) continue;
      try {
        onEvent(eventMatch[1].trim(), JSON.parse(dataMatch[1]));
      } catch {
        // Skip malformed events
      }
    }
  }
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttachments, setHasAttachments] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState("claude-sonnet");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [streamingMessage, setStreamingMessage] = useState<StreamingState | null>(null);

  /** Load an existing conversation by ID. */
  const loadConversation = useCallback(async (id: string) => {
    try {
      const data = await apiFetch<ConversationDetail>(`/api/conversations/${id}`);
      setConversationId(id);
      setModel(data.model);
      setMessages(data.messages.map((m) => toLocalMessage(m, id)));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const sendMessage = useCallback(
    async (content: string, attachments?: Attachment[]) => {
      if (!content.trim() || isLoading) return;

      setError(null);
      setHasAttachments(!!(attachments?.length));

      // Build attachment metadata for display (no base64 data)
      const attachmentsMeta = attachments?.map((a) => ({
        type: a.type,
        mediaType: a.mediaType,
        filename: a.filename,
        sizeBytes: a.sizeBytes,
      }));

      // Add optimistic user message
      const userMsg: ChatMessage = {
        id: nextId(),
        role: "user",
        content: content.trim(),
        attachments: attachmentsMeta,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);
      setStreamingMessage({ content: "", liveTools: [], liveCharts: [] });

      try {
        // Create conversation on first message
        let convId = conversationId;
        if (!convId) {
          const conv = await apiMutate<{ id: string; model: string }>(
            "/api/conversations",
            "POST",
            { model }
          );
          convId = conv.id;
          setConversationId(convId);
        }

        // Try SSE streaming first
        const resp = await fetch(`/api/conversations/${convId}/messages`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "Accept": "text/event-stream",
          },
          body: JSON.stringify({
            content: content.trim(),
            model,
            ...(attachments?.length ? { attachments } : {}),
          }),
        });

        if (!resp.ok) {
          const text = await resp.text();
          throw new Error(`API ${resp.status}: ${text}`);
        }

        // Check if we got SSE or JSON fallback
        const contentType = resp.headers.get("content-type") || "";

        if (contentType.includes("text/event-stream") && resp.body) {
          // SSE streaming path
          const reader = resp.body.getReader();

          await readSSEStream(reader, (event, data) => {
            switch (event) {
              case "text_delta":
                setStreamingMessage((prev) => prev ? {
                  ...prev,
                  content: prev.content + (data.text as string),
                } : prev);
                break;

              case "tool_start":
                setStreamingMessage((prev) => prev ? {
                  ...prev,
                  liveTools: [...prev.liveTools, {
                    tool: data.tool as string,
                    input_summary: data.input_summary as string,
                    status: "running",
                  }],
                } : prev);
                break;

              case "tool_result":
                setStreamingMessage((prev) => {
                  if (!prev) return prev;
                  const tools = [...prev.liveTools];
                  // Find the last running tool with this name
                  for (let i = tools.length - 1; i >= 0; i--) {
                    if (tools[i].tool === data.tool && tools[i].status === "running") {
                      tools[i] = {
                        ...tools[i],
                        status: "done",
                        row_count: data.row_count as number | undefined,
                        execution_ms: data.execution_ms as number | undefined,
                      };
                      break;
                    }
                  }
                  return { ...prev, liveTools: tools };
                });
                break;

              case "tool_chart":
                setStreamingMessage((prev) => prev ? {
                  ...prev,
                  liveCharts: [...prev.liveCharts, {
                    spec: data.chart,
                    sqlQuery: data.sql_query as string | undefined,
                  }],
                } : prev);
                break;

              case "done": {
                const assistantMsg: ChatMessage = {
                  id: data.id as string,
                  role: "assistant",
                  content: data.content as string,
                  tool_calls: data.tool_calls as ToolCallInfo[],
                  model: data.model as string,
                  duration_ms: data.duration_ms as number,
                  timestamp: Date.now(),
                  conversationId: convId!,
                  follow_up_suggestions: data.follow_up_suggestions as string[],
                };
                setMessages((prev) => [...prev, assistantMsg]);
                setStreamingMessage(null);
                setIsLoading(false);
                setHasAttachments(false);
                break;
              }

              case "error":
                setError(data.message as string);
                setStreamingMessage(null);
                setIsLoading(false);
                setHasAttachments(false);
                break;
            }
          });

          // If stream ended without done event, clean up
          setStreamingMessage((prev) => {
            if (prev) {
              setIsLoading(false);
              setHasAttachments(false);
            }
            return null;
          });
        } else {
          // JSON fallback path (shouldn't happen normally, but safe)
          const data: SendMessageResponse = await resp.json();
          const assistantMsg: ChatMessage = {
            id: data.id,
            role: "assistant",
            content: data.content,
            tool_calls: data.tool_calls,
            model: data.model,
            duration_ms: data.duration_ms,
            timestamp: Date.now(),
            conversationId: convId,
            follow_up_suggestions: data.follow_up_suggestions,
          };
          setMessages((prev) => [...prev, assistantMsg]);
          setStreamingMessage(null);
        }
      } catch (err) {
        setError((err as Error).message);
        setStreamingMessage(null);
      } finally {
        setIsLoading(false);
        setHasAttachments(false);
      }
    },
    [messages, model, isLoading, conversationId]
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    setStreamingMessage(null);
  }, []);

  return {
    messages,
    isLoading,
    hasAttachments,
    error,
    model,
    setModel,
    sendMessage,
    clearChat,
    conversationId,
    loadConversation,
    streamingMessage,
  };
}

export function useChatModels() {
  const [models, setModels] = useState<ModelInfo[]>([]);

  const fetchModels = useCallback(async () => {
    try {
      const data = await apiFetch<ModelInfo[]>("/api/chat/models");
      setModels(data);
    } catch {
      // Fallback to hardcoded list
      setModels([
        { id: "claude-opus", name: "Claude Opus", provider: "anthropic" },
        { id: "claude-sonnet", name: "Claude Sonnet", provider: "anthropic" },
        { id: "claude-haiku", name: "Claude Haiku", provider: "anthropic" },
      ]);
    }
  }, []);

  return { models, fetchModels };
}
